import * as React from 'react'

import { Box, Button, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/Popover'
import ScrollableList from 'modules/core/components/ScrollableList'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import getTitle from 'modules/documents/components/Documents/getTitle'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { sortFoldersByName } from 'modules/documents/lib/Folders'
import { spaceToDescription, spacesFromFolders } from 'modules/documents/lib/Spaces'
import { SPACE, SPACES } from 'modules/documents/lib/constants'

import * as styles from './index.vanilla.css'

const ALL_SPACES = SPACES.UNKNOWN
const NO_FOLDER_FILTER_ID = -1

interface FolderInfo {
  id: number
  name: string
  parentFolderId?: number
  directChildren?: number[]
}

interface Props {
  label?: string
  readOnly?: boolean
  noneOption?: boolean
  initialFolder?: Folder
  initialSpace?: SPACE
  folders: Folder[]
  onChange: (value: number | null) => void
  hideSelectInput?: boolean
  tooltip?: string
}

const FolderSelector = ({
  label,
  readOnly = false,
  noneOption = true,
  initialFolder,
  initialSpace,
  folders,
  onChange,
  hideSelectInput = false,
  tooltip,
}: Props) => {
  const initialTextLabel = initialFolder
    ? getTitle(initialFolder.name)
    : i18n.t('split_documents.form.none')
  const [isOpen, toggleOpen] = useToggle(false)
  const [openInfo, toggleOpenInfo] = useToggle(false)
  const [selectButtonDisabled, setSelectButtonDisabled] = React.useState<boolean>(true)
  const [textLabel, setTextLabel] = React.useState<string>(initialTextLabel)
  const [selectedFolder, setSelectedFolder] = React.useState<FolderInfo | null>()
  const [visitedFolder, setVisitedFolder] = React.useState<FolderInfo | null>(null)
  const [foldersToDisplay, setFoldersToDisplay] = React.useState<Map<number, FolderInfo>>(new Map())
  const allFolders = useLazyRef<Map<number, FolderInfo>>(
    () => new Map<number, FolderInfo>()
  ).current
  const rootFolders = useLazyRef<Map<number, FolderInfo>>(
    () => new Map<number, FolderInfo>()
  ).current
  const spacesToShow = spacesFromFolders(initialFolder ? [initialFolder] : folders)
  const showSpacesLevel = spacesToShow.length > 1
  const initSpace = initialSpace || (showSpacesLevel ? ALL_SPACES : spacesToShow[0] || ALL_SPACES)
  const [currentSpace, setCurrentSpace] = React.useState<SPACE>(initSpace)
  const spaceDescriptionText = spaceToDescription(currentSpace)

  useUnsafeEffect(() => {
    if (currentSpace === ALL_SPACES) {
      allFolders.clear()
      rootFolders.clear()
      return
    }

    if (noneOption) {
      const noneFolder = {
        id: NO_FOLDER_FILTER_ID,
        name: i18n.t('split_documents.form.none'),
      }

      allFolders.set(NO_FOLDER_FILTER_ID, noneFolder)
      rootFolders.set(NO_FOLDER_FILTER_ID, noneFolder)
    }

    const filteredFoldersBySpace = folders.filter((folder) => folder.space === currentSpace)
    const sortedFolders = sortFoldersByName(filteredFoldersBySpace)

    sortedFolders.forEach((folder) => {
      const directSubfoldersIds = filteredFoldersBySpace
        .filter((subfolder) => subfolder.parentFolderId === folder.id)
        .map((subfolder) => subfolder.id)

      const folderInfo = {
        id: folder.id,
        name: folder.name,
        parentFolderId: folder.parentFolderId ?? undefined,
        directChildren: directSubfoldersIds,
      }

      allFolders.set(folder.id, folderInfo)

      if (!folder.parentFolderId) {
        rootFolders.set(folder.id, folderInfo)
      }
    })

    setFoldersToDisplay(rootFolders)
    setSelectedFolder(null)
    setVisitedFolder(null)
    initialSetup()
  }, [folders, initialFolder, currentSpace])

  const initialSetup = () => {
    if (!initialFolder) return
    setSelectedFolder(allFolders.get(initialFolder.id))

    if (!initialFolder.parentFolderId) {
      setFoldersToDisplay(rootFolders)
      return
    }

    const foldersToShow = new Map<number, FolderInfo>()
    const parentFolder = allFolders.get(initialFolder.parentFolderId)
    const directChildrenIds = parentFolder?.directChildren || []

    directChildrenIds.forEach((id) => {
      const folderInfo = allFolders.get(id)
      if (!folderInfo) return

      foldersToShow.set(id, folderInfo)
    })

    setFoldersToDisplay(foldersToShow)

    if (parentFolder) {
      setVisitedFolder(parentFolder)
    }
  }

  const onSelectFolder = (folder: FolderInfo) => {
    if (selectedFolder === folder) {
      setSelectedFolder(null)
      setSelectButtonDisabled(true)
    } else {
      setSelectedFolder(folder)
      setSelectButtonDisabled(false)
    }
  }

  const confirmFolderSelection = () => {
    if (!selectedFolder) return

    // README: `folder_id: -1` is used to manage the None Folder option (`folder_id: null`)
    const selectedFolderId = selectedFolder.id === NO_FOLDER_FILTER_ID ? null : selectedFolder.id

    toggleOpen(false)()
    setTextLabel(getTitle(selectedFolder.name))
    setSelectButtonDisabled(true)
    setFoldersToDisplay(rootFolders)
    setSelectedFolder(null)
    setVisitedFolder(null)

    onChange(selectedFolderId)
  }

  const retrieveFoldersInfo = (folderIds: number[]) => {
    const foldersMap = new Map<number, FolderInfo>()

    folderIds.forEach((id) => {
      const folderInfo = allFolders.get(id)
      if (!folderInfo) return

      foldersMap.set(id, folderInfo)
    })

    return foldersMap
  }

  const updateFolders = (folder: FolderInfo) => {
    setSelectButtonDisabled(true)
    setSelectedFolder(null)
    setVisitedFolder(folder)

    const directChildrenIds = allFolders.get(folder.id)?.directChildren || []
    setFoldersToDisplay(retrieveFoldersInfo(directChildrenIds))
  }

  const hasChildren = (value: FolderInfo) => !!value.directChildren?.length

  const goBack = () => {
    setSelectedFolder(null)

    if (!visitedFolder) {
      setFoldersToDisplay(new Map())
      setCurrentSpace(ALL_SPACES)
    }

    if (visitedFolder) {
      setSelectButtonDisabled(true)
      const immediateParentId = visitedFolder.parentFolderId
      if (!immediateParentId) {
        setFoldersToDisplay(rootFolders)
        setVisitedFolder(null)
        return
      }

      const immediateParent = allFolders.get(immediateParentId)
      if (immediateParent) {
        setVisitedFolder(immediateParent)
        const folderIds = immediateParent.directChildren || []
        const directSubfolders = retrieveFoldersInfo(folderIds)
        setFoldersToDisplay(directSubfolders)
      }
    }
  }

  const visitedFolderParentText = () => {
    if (!visitedFolder) return i18n.t('split_documents.form.back')

    if (!visitedFolder.parentFolderId) return spaceDescriptionText

    const immediateParentId = visitedFolder.parentFolderId
    const immediateParent = allFolders.get(immediateParentId)

    if (immediateParent) return getTitle(immediateParent.name)
    return spaceDescriptionText
  }

  const selectFolderText = () => {
    if (selectedFolder) {
      return i18n.t('split_documents.form.select_folder_name', {
        folder_name: getTitle(selectedFolder.name),
      })
    }
    return i18n.t('split_documents.form.select_folder')
  }

  const visitedFolderText = visitedFolder ? getTitle(visitedFolder.name) : spaceDescriptionText

  const backgroundColor = (folderId: number) => {
    if (selectedFolder?.id === folderId) return 'viridian100'
    return
  }

  const textColor = (folderId: number) => {
    if (selectedFolder?.id === folderId) return 'viridian1200'
    return
  }

  const folderNavigation = (
    <Box>
      <Box
        paddingX="s16"
        paddingY="s8"
        gap="s12"
        background="grey100"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Button
          onClick={() => goBack()}
          type="secondary"
          disabled={initialSpace === currentSpace && !visitedFolder}
          iconName="chevron-left"
        >
          {visitedFolderParentText()}
        </Button>
        <Box flexDirection="row" gap="s4" alignItems="center">
          <Icon.Medium icon="folder" />
          <Text size="200">{visitedFolderText}</Text>
        </Box>
      </Box>
      <Box gap="s12">
        <ScrollableList size="small">
          <Box component="ul">
            {!foldersToDisplay.size &&
              showSpacesLevel &&
              spacesToShow.map((spaceToShow) => (
                <Box
                  component="li"
                  key={spaceToShow}
                  className={styles.folder}
                  flexDirection="row"
                  alignItems="center"
                >
                  <Box padding="s16" width="full" textAlign="left">
                    <Text size="200" ellipsis>
                      {spaceToDescription(spaceToShow)}
                    </Text>
                  </Box>
                  <Box
                    paddingX="s16"
                    paddingY="s6"
                    cursor="pointer"
                    onClick={() => setCurrentSpace(spaceToShow)}
                  >
                    <Box className="arrow" borderRadius={{ all: 'abs100' }}>
                      <Icon.Medium icon="chevron-right" color="grey600" />
                    </Box>
                  </Box>
                </Box>
              ))}
            {[...foldersToDisplay.values()].map((folder) => {
              const currentFolder = foldersToDisplay.get(folder.id)
              if (!currentFolder) return

              return (
                <Box
                  component="li"
                  className={styles.folder}
                  background={backgroundColor(currentFolder.id)}
                  key={currentFolder.id}
                  flexDirection="row"
                  alignItems="center"
                >
                  <Box
                    cursor="pointer"
                    onClick={() => onSelectFolder(currentFolder)}
                    paddingY="s12"
                    paddingX="s16"
                    width="full"
                    textAlign="left"
                  >
                    <Text color={textColor(currentFolder.id)} size="200" ellipsis>
                      {getTitle(currentFolder.name)}
                    </Text>
                  </Box>
                  {hasChildren(currentFolder) && (
                    <Box
                      paddingX="s16"
                      paddingY="s6"
                      cursor="pointer"
                      onClick={() => updateFolders(currentFolder)}
                      aria-label={i18n.t('split_documents.form.show_subfolders')}
                    >
                      <Box className="arrow" borderRadius={{ all: 'abs100' }}>
                        <Icon.Medium icon="chevron-right" color="grey600" />
                      </Box>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </ScrollableList>
      </Box>
      <button
        className={styles.confirmFilter}
        onClick={confirmFolderSelection}
        disabled={selectButtonDisabled}
      >
        <Text.Normal
          size="300"
          weight="medium"
          color={selectButtonDisabled ? 'grey600' : 'grey800'}
        >
          {selectFolderText()}
        </Text.Normal>
      </button>
    </Box>
  )

  if (readOnly || foldersToDisplay.size === 0)
    return <TextField.Select label={label} value={textLabel} />

  if (hideSelectInput) return folderNavigation

  return (
    <Popover
      attachment="top center"
      size="bigger"
      open={isOpen}
      toggle={toggleOpen}
      onOutsideClick={() => {
        initialSetup()
      }}
    >
      <Box flexDirection="column" gap="s8">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <Box aria-label={label}>
            <Text.Normal size="200" color="grey1000" weight="medium">
              {label}
            </Text.Normal>
          </Box>
          {tooltip && (
            <Tooltip.Alt
              attachment="bottom center"
              targetAttachment="top center"
              open={openInfo}
              toggle={toggleOpenInfo}
              label={tooltip}
            >
              <Icon.Medium icon="info-circle" color="grey800" />
            </Tooltip.Alt>
          )}
        </Box>
        <TextField.Select value={textLabel} />
      </Box>
      {folderNavigation}
    </Popover>
  )
}

export default FolderSelector
